import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaFileContract,
  FaUserGraduate,
  FaCheck,
  FaChevronDown,
  FaEye,
  FaDownload,
  FaFilePdf,
  FaFileWord,
  FaFileAlt
} from 'react-icons/fa';
import Toast from '../components/Toast';
import * as authService from '../services/authService';
import * as adminService from '../services/adminService';
import * as masterService from '../services/masterService';

const AssignAbstract = () => {
  const [abstracts, setAbstracts] = useState([]);
  const [professors, setProfessors] = useState([]);
  const [masterSpecs, setMasterSpecs] = useState([]);
  const [selectedProfessors, setSelectedProfessors] = useState({});
  const [reviewerSearches, setReviewerSearches] = useState({});
  const [showDropdowns, setShowDropdowns] = useState({});
  const [reassigning, setReassigning] = useState({});
  const [membersMap, setMembersMap] = useState({});
  const [selectedAbstract, setSelectedAbstract] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [toast, setToast] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const currentUser = authService.getCurrentUser();

    if (!currentUser) {
      navigate('/login');
      return;
    }

    if (currentUser.role !== 'admin') {
      navigate('/');
      return;
    }

    loadAbstracts();
    loadProfessors();
    loadMasterData();
  }, [navigate]);

  const loadMasterData = async () => {
    try {
      const response = await masterService.getAllSpecializations();
      const data = response.data || response || [];
      setMasterSpecs(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading master data:', error);
    }
  };

  const loadAbstracts = async () => {
    try {
      const response = await adminService.getJournalSubmissions();
      const allAbstracts = response.data || response || [];

      // Sort by submission date in descending order (LIFO - newest first)
      const sortedAbstracts = [...allAbstracts].sort((a, b) => {
        const dateA = new Date(a.created_at || a.submittedAt);
        const dateB = new Date(b.created_at || b.submittedAt);
        return dateB - dateA;
      });

      setAbstracts(sortedAbstracts);
    } catch (error) {
      console.error('Error loading abstracts:', error);
      showToast('Failed to load abstracts', 'error');
    }
  };

  const loadProfessors = async () => {
    try {
      const response = await masterService.getMembers();
      const data = response.data || response || {};

      const reviewers = Array.isArray(data.reviewers) ? data.reviewers : [];
      const eb = Array.isArray(data.editorial_board || data.editorialBoard)
        ? (data.editorial_board || data.editorialBoard) : [];
      const subEb = Array.isArray(data.sub_eb_members || data.subEB)
        ? (data.sub_eb_members || data.subEB) : [];

      // Flatten for the select dropdown
      const combined = [
        ...reviewers.map(m => ({ ...m, displayRole: 'Reviewer' })),
        ...eb.map(m => ({ ...m, displayRole: 'EB Member' })),
        ...subEb.map(m => ({ ...m, displayRole: 'Sub-EB Member' }))
      ];

      setProfessors(combined);

      const map = {};
      combined.forEach(m => {
        if (m.id) {
          map[m.id] = m.full_name || m.name;
        }
      });
      setMembersMap(map);
    } catch (error) {
      console.error('Error loading members:', error);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const handleProfessorSelect = (abstractId, professor) => {
    setSelectedProfessors({
      ...selectedProfessors,
      [abstractId]: professor.id
    });
    setReviewerSearches({
      ...reviewerSearches,
      [abstractId]: professor.full_name || professor.name
    });
    setShowDropdowns({
      ...showDropdowns,
      [abstractId]: false
    });
  };

  const handleSearchChange = (abstractId, value) => {
    setReviewerSearches({
      ...reviewerSearches,
      [abstractId]: value
    });
    setShowDropdowns({
      ...showDropdowns,
      [abstractId]: true
    });
    // Reset selection if typing
    if (selectedProfessors[abstractId]) {
      const newSelections = { ...selectedProfessors };
      delete newSelections[abstractId];
      setSelectedProfessors(newSelections);
      setShowDropdowns({ ...showDropdowns, [abstractId]: true });
    }
  };

  const getFileIcon = (fileName) => {
    if (!fileName) return null;
    const extension = fileName.split('.').pop().toLowerCase();
    if (extension === 'pdf') {
      return <FaFilePdf className="text-red-500 text-xl" />;
    } else if (extension === 'doc' || extension === 'docx') {
      return <FaFileWord className="text-blue-500 text-xl" />;
    }
    return <FaFileAlt className="text-gray-500 text-xl" />;
  };

  const isDownloadableFile = (fileName) => {
    if (!fileName) return false;
    const extension = fileName.split('.').pop().toLowerCase();
    return extension === 'pdf' || extension === 'doc' || extension === 'docx';
  };

  const handleDownloadFile = (abstract) => {
    const fileUrl = abstract.file_url || (abstract.versions && abstract.versions[0]?.file_url);
    if (fileUrl) {
      try {
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = fileUrl.split('/').pop();
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showToast('Starting download...', 'success');
      } catch (error) {
        console.error('Download error:', error);
        showToast('Could not download file.', 'error');
      }
    } else {
      showToast('No file available.', 'error');
    }
  };

  const handlePreviewClick = (abstract) => {
    setSelectedAbstract(abstract);
    setShowPreviewModal(true);
  };

  const handleAssignSubmit = async (abstract) => {
    const selectedProfessorId = selectedProfessors[abstract.id];

    if (!selectedProfessorId) {
      showToast('Please select a professor', 'error');
      return;
    }

    const professor = professors.find(p => String(p.id) === String(selectedProfessorId));

    try {
      const payload = {
        submission_id: abstract.id,
        reviewer_id: selectedProfessorId,
        reviewer_name: professor.full_name || professor.name
      };

      if (reassigning[abstract.id] || (abstract.status && abstract.status.includes('reject'))) {
        await adminService.reassignReviewer(payload);
      } else {
        try {
          await adminService.assignReviewer(payload);
        } catch (err) {
          // If first assign fails, fallback to reassign
          await adminService.reassignReviewer(payload);
        }
      }

      showToast(`Abstract assigned to ${professor.full_name || professor.name} successfully!`, 'success');

      // Reload abstracts to show updated status
      loadAbstracts();

      // Clear selection for this abstract
      const newSelections = { ...selectedProfessors };
      delete newSelections[abstract.id];
      setSelectedProfessors(newSelections);

      const newSearches = { ...reviewerSearches };
      delete newSearches[abstract.id];
      setReviewerSearches(newSearches);

      const newReassigning = { ...reassigning };
      delete newReassigning[abstract.id];
      setReassigning(newReassigning);
    } catch (error) {
      console.error('Error assigning reviewer:', error);
      showToast(error.message || 'Failed to assign reviewer', 'error');
    }
  };

  return (
    <>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-indigo-50 py-6">
        <div className="max-w mx-auto px-4">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl shadow-lg p-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl lg:text-4xl font-bold text-white mb-2">Assign Abstracts</h1>
                <p className="text-purple-100">Assign submitted abstracts to professors or reviewers for review</p>
              </div>
              <div className="hidden md:block">
                <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                  <FaFileContract className="text-3xl text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Abstracts List */}
          {abstracts.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
              <FaFileContract className="text-6xl text-gray-300 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">No Abstracts Yet</h3>
              <p className="text-gray-600">No abstracts have been submitted for review.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {abstracts.map((abstract) => (
                <div
                  key={abstract.id}
                  className={`bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all border-l-4 border-purple-500 ${showDropdowns[abstract.id] ? 'z-50 relative' : 'z-10 relative'
                    }`}
                >
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-8 gap-4 items-start">
                      {/* 1. Abstract Title & Authors */}
                      <div className="lg:col-span-1">
                        <p className="text-xs text-gray-500 mb-1 font-semibold uppercase tracking-wider">Abstract Info</p>
                        <p className="font-semibold text-gray-900 text-sm line-clamp-2 mb-1" title={abstract.title || abstract.abstractTitle}>
                          {abstract.title || abstract.abstractTitle}
                        </p>
                        <p className="text-sm text-gray-700 line-clamp-2 italic font-medium">
                          By: {[
                            abstract.submitter?.full_name || abstract.submitter?.name || abstract.user?.full_name || 'Author',
                            ...(() => {
                              const extra = abstract.authors || abstract.co_authors || [];
                              try {
                                if (typeof extra === 'string') {
                                  const parsed = JSON.parse(extra);
                                  return Array.isArray(parsed) ? parsed : [parsed];
                                }
                                return Array.isArray(extra) ? extra : [extra];
                              } catch (e) {
                                return typeof extra === 'string' ? extra.split(',').map(s => ({ name: s.trim() })) : [];
                              }
                            })().map(a => (a ? (typeof a === 'string' ? a : (a.full_name || a.name)) : null)).filter(Boolean)
                          ].join(', ')}
                        </p>
                      </div>

                      {/* 2. Department Div */}
                      <div className="bg-purple-50 p-3 rounded-lg border border-purple-100 min-h-[85px] flex flex-col justify-center">
                        <p className="text-[10px] text-purple-600 mb-1 font-bold uppercase tracking-tight">Department</p>
                        <p className="text-xs font-bold text-purple-900 leading-tight">
                          {abstract.subject?.name || abstract.subject_name || abstract.departmentName || abstract.department || 'General'}
                        </p>
                      </div>

                      {/* 3. Stream Div */}
                      <div className="bg-blue-50 p-3 rounded-lg border border-blue-100 min-h-[85px] flex flex-col justify-center">
                        <p className="text-[10px] text-blue-600 mb-1 font-bold uppercase tracking-tight">Stream</p>
                        <p className="text-xs font-bold text-blue-900 leading-tight">
                          {abstract.stream?.name || abstract.stream_name || abstract.streamName || abstract.stream || abstract.major || 'N/A'}
                        </p>
                      </div>

                      {/* 4. Specialization Div */}
                      <div className="bg-teal-50 p-3 rounded-lg border border-teal-100 min-h-[85px] flex flex-col justify-center">
                        <p className="text-[10px] text-teal-600 mb-1 font-bold uppercase tracking-tight">Specialization</p>
                        <p className="text-xs font-bold text-teal-900 leading-tight">
                          {abstract.specialization?.name ||
                            abstract.specialization_name ||
                            abstract.specialisation_name ||
                            abstract.specializationName ||
                            abstract.specialisationName ||
                            (abstract.specialization_id ? masterSpecs.find(s => String(s.id) === String(abstract.specialization_id))?.name : null) ||
                            abstract.specialization ||
                            abstract.specialisation ||
                            'N/A'}
                        </p>
                      </div>

                      {/* 5. Suggested Reviewers Div */}
                      <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-100 min-h-[85px] flex flex-col justify-center">
                        <p className="text-[10px] text-yellow-600 mb-1 font-bold uppercase tracking-tight">Suggested Reviewers</p>
                        <p className="text-xs font-bold text-yellow-900 leading-tight">
                          {(() => {
                            const raw = abstract.suggested_reviewers || abstract.suggested_reviewer_details || abstract.selectedReviewers || abstract.suggested_reviewer_ids || [];
                            let list = [];
                            if (typeof raw === 'string' && raw.trim()) {
                              try {
                                const parsed = JSON.parse(raw);
                                list = Array.isArray(parsed) ? parsed : (parsed ? [parsed] : []);
                              } catch (e) {
                                list = [raw];
                              }
                            } else {
                              list = Array.isArray(raw) ? raw : (raw ? [raw] : []);
                            }

                            if (list.length === 0) return 'None';

                            return list.map(r => {
                              if (typeof r === 'string' || typeof r === 'number') {
                                return membersMap[r] || r;
                              }
                              return r.full_name || r.name || 'Unknown';
                            }).join(', ');
                          })()}
                        </p>
                      </div>

                      {/* 6. Content Div */}
                      <div className="bg-green-50 p-3 rounded-lg border border-green-100 min-h-[85px] flex flex-col justify-center">
                        <p className="text-[10px] text-green-600 mb-1 font-bold uppercase tracking-tight">Content</p>
                        {(() => {
                          const fileUrl = abstract.file_url || (abstract.versions && abstract.versions[0]?.file_url);
                          const hasText = abstract.abstract_text || abstract.abstractText;

                          if (!fileUrl && !hasText) return <p className="text-xs font-bold text-gray-400">No content</p>;

                          return (
                            <div className="flex flex-col gap-2">
                              {fileUrl && (
                                <div className="flex items-center gap-1">
                                  {getFileIcon(fileUrl)}
                                  <span className="text-[10px] font-bold text-gray-700 truncate max-w-[100px]">{fileUrl.split('/').pop()}</span>
                                </div>
                              )}
                              <button
                                onClick={() => handlePreviewClick(abstract)}
                                className="w-full flex items-center justify-center gap-1 py-1 bg-green-600 text-white text-[9px] rounded font-bold uppercase hover:bg-green-700 transition-all"
                              >
                                <FaEye size={10} />
                                View Content
                              </button>
                            </div>
                          );
                        })()}
                      </div>

                      {/* 5. Action Area (Status or Controls) */}
                      <div className="lg:col-span-2">
                        {(abstract.assigned_reviewer || abstract.assigned_reviewer_id || abstract.reviewer || abstract.assignedTo) && !reassigning[abstract.id] ? (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center justify-between gap-3 min-h-[85px]">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center text-green-600 shrink-0">
                                <FaCheck size={12} />
                              </div>
                              <div className="min-w-0">
                                <p className="text-[10px] text-green-700 font-bold uppercase tracking-wider">Assigned To</p>
                                <p className="text-sm text-gray-900 font-bold">
                                  {(() => {
                                    const r = abstract.assigned_reviewer || abstract.reviewer || abstract.assignedTo;
                                    if (typeof r === 'object' && r !== null) {
                                      return r.full_name || r.name || r.professorName || 'Reviewer';
                                    }
                                    return membersMap[abstract.assigned_reviewer_id || abstract.assigned_reviewer] || 'Reviewer';
                                  })()}
                                </p>
                                <p className="text-[9px] text-gray-500 truncate mt-0.5">
                                  Assigned on {new Date(abstract.assignedAt || abstract.assignedTo?.assignedAt || abstract.updated_at).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={() => setReassigning({ ...reassigning, [abstract.id]: true })}
                              className="text-[10px] text-blue-600 font-bold hover:underline bg-blue-50 px-2 py-1 rounded"
                            >
                              Change
                            </button>
                          </div>
                        ) : (
                          <div className="flex flex-col gap-2 relative">
                            <div className="relative">
                              <input
                                type="text"
                                placeholder="Search reviewer..."
                                value={reviewerSearches[abstract.id] || ''}
                                onChange={(e) => handleSearchChange(abstract.id, e.target.value)}
                                onFocus={() => setShowDropdowns({ ...showDropdowns, [abstract.id]: true })}
                                onBlur={() => setTimeout(() => setShowDropdowns(prev => ({ ...prev, [abstract.id]: false })), 200)}
                                className="w-full pl-3 pr-8 py-2 border-2 border-gray-100 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white transition-all text-xs font-semibold"
                              />
                              <FaChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none text-[10px]" />

                              {showDropdowns[abstract.id] && (
                                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                                  {professors
                                    .filter(p => (p.full_name || p.name || '').toLowerCase().includes((reviewerSearches[abstract.id] || '').toLowerCase()))
                                    .map((professor) => (
                                      <div
                                        key={professor.id}
                                        onMouseDown={() => handleProfessorSelect(abstract.id, professor)}
                                        className="px-3 py-2 hover:bg-purple-50 cursor-pointer text-sm font-semibold border-b border-gray-50 last:border-0"
                                      >
                                        <div className="flex justify-between items-center">
                                          <span>{professor.full_name || professor.name}</span>
                                          <span className="text-[9px] bg-gray-100 px-1 rounded text-gray-500">{professor.displayRole}</span>
                                        </div>
                                      </div>
                                    ))}
                                  {professors.filter(p => (p.full_name || p.name || '').toLowerCase().includes((reviewerSearches[abstract.id] || '').toLowerCase())).length === 0 && (
                                    <div className="px-3 py-2 text-gray-400 text-xs italic text-center">No reviewers found</div>
                                  )}
                                </div>
                              )}
                            </div>

                            <button
                              onClick={() => handleAssignSubmit(abstract)}
                              disabled={!selectedProfessors[abstract.id]}
                              className={`w-full flex items-center justify-center gap-2 py-2 rounded-lg font-bold transition-all text-[10px] uppercase tracking-wider ${selectedProfessors[abstract.id]
                                ? 'bg-purple-600 text-white hover:bg-purple-700 shadow-md transform active:scale-[0.98]'
                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                }`}
                            >
                              <FaUserGraduate />
                              Assign
                            </button>
                            {reassigning[abstract.id] && (
                              <button
                                onClick={() => setReassigning({ ...reassigning, [abstract.id]: false })}
                                className="w-full py-1 text-[9px] text-gray-400 hover:text-gray-600 font-bold uppercase tracking-tighter"
                              >
                                Cancel
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Preview Modal */}
      {showPreviewModal && selectedAbstract && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full my-8 max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-6 rounded-t-2xl sticky top-0 z-10">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">Abstract Preview</h2>
                  <p className="text-purple-100">{selectedAbstract.title || selectedAbstract.abstractTitle}</p>
                </div>
                <button
                  onClick={() => {
                    setShowPreviewModal(false);
                    setSelectedAbstract(null);
                  }}
                  className="text-white hover:bg-white/20 p-2 rounded-lg transition-all"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-8">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Abstract Content</h3>

                {(() => {
                  const fileUrl = selectedAbstract.file_url || (selectedAbstract.versions && selectedAbstract.versions[0]?.file_url);
                  const abstractText = selectedAbstract.abstract_text || selectedAbstract.abstractText;

                  return (
                    <>
                      {fileUrl && (
                        <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl border-2 border-purple-200 mb-6">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                              {getFileIcon(fileUrl)}
                              <div>
                                <p className="font-semibold text-gray-900">{fileUrl.split('/').pop()}</p>
                                <p className="text-sm text-gray-600">Attached File</p>
                              </div>
                            </div>
                            <button
                              onClick={() => handleDownloadFile(selectedAbstract)}
                              className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-pink-500 transition-all font-bold"
                            >
                              <FaDownload />
                              Download
                            </button>
                          </div>
                        </div>
                      )}

                      {abstractText && (
                        <div className="bg-white p-6 rounded-xl border-2 border-gray-100 shadow-inner max-h-[400px] overflow-y-auto">
                          <p className="text-gray-800 leading-relaxed whitespace-pre-wrap font-medium">
                            {abstractText}
                          </p>
                        </div>
                      )}

                      {!fileUrl && !abstractText && (
                        <div className="text-center py-12 bg-gray-50 rounded-xl">
                          <FaFileContract className="text-6xl text-gray-300 mx-auto mb-4" />
                          <p className="text-gray-500 font-medium">No content available for this submission.</p>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t border-gray-200 p-6 rounded-b-2xl">
              <button
                onClick={() => {
                  setShowPreviewModal(false);
                  setSelectedAbstract(null);
                }}
                className="w-full px-6 py-3 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300 transition-all"
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AssignAbstract;